import React, { Suspense, lazy, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import DeferredBoundary from './components/DeferredBoundary.jsx'
import Navbar from './components/Navbar.jsx'
import { WishlistProvider } from './context/WishlistContext.jsx'
import { supabase } from './supabaseClient.js'
import {
  resolveCheckoutVariantId,
  resolveVariantPricing,
} from './utils/shopify.js'
import {
  cancelNonCriticalTask,
  scheduleNonCriticalTask,
} from './utils/nonCritical.js'

const RootLayout = lazy(() => import('./RootLayout.jsx'))
const HomePage = lazy(() => import('./pages/HomePage.jsx'))
const AboutUsPage = lazy(() => import('./pages/AboutUsPage.jsx'))
const CustomVibePage = lazy(() => import('./pages/CustomVibePage.jsx'))
const CollectionSearchPage = lazy(() =>
  import('./pages/CollectionSearchPage.jsx'),
)
const CollectionPage = lazy(() => import('./pages/CollectionPage.jsx'))
const AccountDashboard = lazy(() =>
  import('./pages/AccountDashboard.jsx'),
)
const InfoPage = lazy(() => import('./pages/InfoPage.jsx'))
const LimitedDropsPage = lazy(() =>
  import('./pages/LimitedDropsPage.jsx'),
)
const OffersPage = lazy(() => import('./pages/OffersPage.jsx'))
const ProductPage = lazy(() => import('./pages/ProductPage.jsx'))
const SalePage = lazy(() => import('./pages/SalePage.jsx'))
const ShopScreen = lazy(() => import('./pages/ShopScreen.jsx'))
const WishlistPage = lazy(() => import('./pages/Wishlist.jsx'))

function DeferredRoute({ children }) {
  return (
    <DeferredBoundary fallback={<div className="min-h-screen bg-black" />}>
      {children}
    </DeferredBoundary>
  )
}

function ScrollToTop() {
  const { key, pathname, search } = useLocation()

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [key, pathname, search])

  return null
}

function App() {
  const location = useLocation()
  const hideNavRoutes = [
    '/checkout',
    '/milieu',
    '/privacy',
    '/refund',
    '/terms',
    '/contact',
    '/hit-us-up',
  ]
  const isHiddenRoute = hideNavRoutes.some((path) =>
    location.pathname.toLowerCase().includes(path),
  )
  const [activeVibe, setActiveVibe] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [cartItems, setCartItems] = useState([])
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [reviews, setReviews] = useState([])
  const urlSearchQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)

    return searchParams.get('q')?.trim() ?? ''
  }, [location.search])

  useLayoutEffect(() => {
    setSearchQuery((currentSearchQuery) =>
      currentSearchQuery === urlSearchQuery
        ? currentSearchQuery
        : urlSearchQuery,
    )
  }, [setSearchQuery, urlSearchQuery])

  useEffect(() => {
    let isActive = true

    async function fetchReviews() {
      try {
        if (!supabase?.from) {
          if (isActive) {
            setReviews([])
          }
          return
        }

        const { data, error } = await supabase
          .from('reviews')
          .select('*')
          .order('created_at', { ascending: false })

        if (!isActive) {
          return
        }

        if (error) {
          throw error
        }

        setReviews(
          Array.isArray(data)
            ? data.map((review) => ({
                ...review,
                source: 'real',
                text:
                  review?.comment ||
                  review?.note ||
                  review?.body ||
                  review?.content ||
                  '',
                name: review?.user_name || review?.name || '',
              }))
            : [],
        )
      } catch (error) {
        if (isActive) {
          console.error(
            'Supabase Fetch Error:',
            error?.message || String(error || 'Failed to load reviews.'),
          )
          setReviews([])
        }
      }
    }

    const reviewsTaskHandle = scheduleNonCriticalTask(() => {
      void fetchReviews()
    }, 1500)

    return () => {
      isActive = false
      cancelNonCriticalTask(reviewsTaskHandle)
    }
  }, [])

  function addToCart(product) {
    if (!product) {
      return
    }

    const variantId = resolveCheckoutVariantId(product)

    if (!variantId) {
      console.error('Missing Variant ID for checkout/cart')
      return
    }

    const pricing = resolveVariantPricing(product)
    const variantPriceValue = pricing.priceAmount || 0

    const normalizedProduct = {
      ...product,
      variantId,
      quantity: Number(product?.quantity ?? 1) || 1,
      merchandiseId: product?.merchandiseId || variantId,
      shopifyVariantId: product?.shopifyVariantId || variantId,
      shopify_variant_id: product?.shopify_variant_id || variantId,
      selected_variant_id: product?.selected_variant_id || variantId,
      variant:
        product?.variant && typeof product.variant === 'object'
          ? {
              ...product.variant,
              id: product?.variant?.id || variantId,
              shopifyVariantId:
                product?.variant?.shopifyVariantId || variantId,
              variantId,
              price: {
                amount: String(variantPriceValue),
                currencyCode:
                  product?.variant?.price?.currencyCode ||
                  product?.priceRange?.minVariantPrice?.currencyCode ||
                  product?.priceRange?.maxVariantPrice?.currencyCode ||
                  '',
              },
              compareAtPrice: pricing.compareAtAmount
                ? {
                    amount: pricing.compareAtAmount,
                    currencyCode:
                      product?.variant?.compareAtPrice?.currencyCode ||
                      product?.compareAtPriceCurrencyCode ||
                      pricing.currencyCode ||
                      '',
                  }
                : null,
            }
          : {
              id: variantId,
              shopifyVariantId: variantId,
              variantId,
              price: {
                amount: String(variantPriceValue),
                currencyCode:
                  product?.priceRange?.minVariantPrice?.currencyCode ||
                  product?.priceRange?.maxVariantPrice?.currencyCode ||
                  '',
              },
              compareAtPrice: pricing.compareAtAmount
                ? {
                    amount: pricing.compareAtAmount,
                    currencyCode:
                      product?.compareAtPriceCurrencyCode ||
                      product?.priceRange?.minVariantPrice?.currencyCode ||
                      product?.priceRange?.maxVariantPrice?.currencyCode ||
                      '',
                  }
                : null,
            },
      compareAtPrice: pricing.compareAtAmount || '',
      base_price: product?.base_price ?? variantPriceValue,
      price_inr: product?.price_inr ?? variantPriceValue,
      price: product?.price ?? variantPriceValue,
      compareAtPriceCurrencyCode:
        product?.compareAtPriceCurrencyCode ?? pricing.currencyCode,
    }

    setCartItems((currentItems) => [...currentItems, normalizedProduct])
    setIsCartOpen(true)
  }

  function removeFromCart(identifierToRemove) {
    setCartItems((currentItems) =>
      currentItems.filter((item, index) => {
        if (typeof identifierToRemove === 'number') {
          return index !== identifierToRemove
        }

        const targetId = String(identifierToRemove ?? '')
        const itemIds = [
          item?.id,
          item?.uuid,
          item?.variantId,
          item?.shopifyVariantId,
          item?.selected_variant_id,
          item?.merchandiseId,
        ]
          .map((value) => String(value ?? ''))
          .filter(Boolean)

        return !itemIds.includes(targetId)
      }),
    )
  }

  function updateCartItemQuantity(indexToUpdate, quantity) {
    const nextQuantity = Math.max(1, Number(quantity) || 1)

    setCartItems((currentItems) =>
      currentItems.map((item, index) =>
        index === indexToUpdate
          ? {
              ...item,
              quantity: nextQuantity,
            }
          : item,
      ),
    )
  }

  function clearCart() {
    setCartItems([])
  }

  const homeScreenElement = (
    <HomePage
      collectionKey={location.key}
      addToCart={addToCart}
      cartItems={cartItems}
      removeFromCart={removeFromCart}
      searchQuery={searchQuery}
      setIsCartOpen={setIsCartOpen}
      updateCartItemQuantity={updateCartItemQuantity}
    />
  )
  const privacyPageElement = (
    <DeferredRoute>
      <InfoPage
        eyebrow="Privacy"
        title="Privacy Policy"
        intro="This page covers how AikoVibe handles customer information, store activity, and support communication across the storefront."
        sections={[
          {
            heading: 'Data We Collect',
            body: 'We may collect details you submit while shopping, checking out, subscribing, or contacting support, including your name, email, phone number, shipping details, and order context.',
          },
          {
            heading: 'How We Use It',
            body: 'That information is used to fulfill orders, provide customer support, send service updates, and improve the shopping experience across the site.',
          },
          {
            heading: 'Questions',
            body: 'For any privacy-related request, reach out to supportaikovibe@gmail.com and include the email address used on your order or subscription.',
          },
        ]}
      />
    </DeferredRoute>
  )
  const refundPageElement = (
    <DeferredRoute>
      <InfoPage
        eyebrow="Refund"
        title="Refund Policy"
        intro="This page outlines the current AikoVibe refund and return support flow for damaged, incorrect, or eligible return cases."
        sections={[
          {
            heading: 'Eligible Requests',
            body: 'If you receive a damaged, defective, or incorrect item, contact support promptly with your order details and clear supporting images so the case can be reviewed.',
          },
          {
            heading: 'Return Window',
            body: 'Requests should be raised within the stated support window after delivery so the team can verify the issue and guide the next steps quickly.',
          },
          {
            heading: 'Support Route',
            body: 'Email supportaikovibe@gmail.com with your order number, issue summary, and any proof needed for review.',
          },
        ]}
      />
    </DeferredRoute>
  )
  const termsPageElement = (
    <DeferredRoute>
      <InfoPage
        eyebrow="Terms"
        title="Terms & Conditions"
        intro="This page covers the basic terms that apply when browsing, purchasing, subscribing, or engaging with AikoVibe services and content."
        sections={[
          {
            heading: 'Orders & Payments',
            body: 'Orders are subject to availability, verification, and successful payment processing. Promotions, timelines, and COD availability may vary by order and serviceable location.',
          },
          {
            heading: 'Store Use',
            body: 'By using the storefront, you agree not to misuse the platform, interfere with checkout systems, or submit fraudulent, abusive, or misleading activity.',
          },
          {
            heading: 'Need Clarification',
            body: 'For order, terms, or platform questions, reach out to supportaikovibe@gmail.com and the team can help you directly.',
          },
        ]}
      />
    </DeferredRoute>
  )
  const contactPageElement = (
    <DeferredRoute>
      <InfoPage
        eyebrow="Support"
        title="Hit Us Up"
        intro="Need help with your order, delivery, exchange, or a general store question? This is the direct customer support page."
        sections={[
          {
            heading: 'Email Support',
            body: 'Write to supportaikovibe@gmail.com for help with orders, refunds, tracking questions, or general support requests.',
          },
          {
            heading: 'What To Include',
            body: 'Share your order number, the email used at checkout, and a short summary of the issue so the team can assist you faster.',
          },
          {
            heading: 'Response Flow',
            body: 'The team reviews requests in order and will reply with the next steps based on the order status, issue type, and verification needed.',
          },
        ]}
      />
    </DeferredRoute>
  )

  return (
    <WishlistProvider>
      <React.Fragment>
        <ScrollToTop />
        {!isHiddenRoute ? (
          <>
            <div className="hidden lg:block">
              <Navbar
                activeVibe={activeVibe}
                setActiveVibe={setActiveVibe}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                cartItems={cartItems}
                setIsCartOpen={setIsCartOpen}
                reviews={reviews}
                setReviews={setReviews}
              />
            </div>
            <div className="hidden h-[100px] w-full shrink-0 bg-black md:h-[130px] lg:block" />
          </>
        ) : null}
        <main className="w-full min-h-screen bg-black">
          <Suspense
            fallback={
              <div
                style={{
                  height: '100vh',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                Loading...
              </div>
            }
          >
            <Routes>
              <Route
                element={
                  <RootLayout
                    cartItems={cartItems}
                    isCartOpen={isCartOpen}
                    setIsCartOpen={setIsCartOpen}
                    removeFromCart={removeFromCart}
                    updateCartItemQuantity={updateCartItemQuantity}
                    clearCart={clearCart}
                  />
                }
              >
                <Route path="/" element={homeScreenElement} />
                <Route path="/privacy" element={privacyPageElement} />
                <Route path="/refund" element={refundPageElement} />
                <Route path="/terms" element={termsPageElement} />
                <Route path="/contact" element={contactPageElement} />
                <Route path="/hit-us-up" element={contactPageElement} />
                <Route
                  path="/products/:handle"
                  element={
                    <DeferredRoute>
                      <ProductPage addToCart={addToCart} />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/collections/limited-drops"
                  element={
                    <DeferredRoute>
                      <LimitedDropsPage
                        activeVibe={activeVibe}
                        collectionKey={location.key}
                        searchQuery={searchQuery}
                        addToCart={addToCart}
                        setActiveVibe={setActiveVibe}
                        setSearchQuery={setSearchQuery}
                      />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/collections/:collectionHandle"
                  element={
                    <DeferredRoute>
                      <CollectionPage
                        activeVibe={activeVibe}
                        collectionKey={location.key}
                        searchQuery={searchQuery}
                        addToCart={addToCart}
                        setActiveVibe={setActiveVibe}
                        setSearchQuery={setSearchQuery}
                      />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/search"
                  element={
                    <DeferredRoute>
                      <CollectionSearchPage
                        activeVibe={activeVibe}
                        collectionKey={location.key}
                        searchQuery={urlSearchQuery}
                        addToCart={addToCart}
                        setActiveVibe={setActiveVibe}
                      />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/collections/all"
                  element={
                    <DeferredRoute>
                      <CollectionSearchPage
                        activeVibe={activeVibe}
                        collectionKey={location.key}
                        searchQuery={urlSearchQuery}
                        addToCart={addToCart}
                        setActiveVibe={setActiveVibe}
                      />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/about"
                  element={
                    <DeferredRoute>
                      <AboutUsPage />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/custom-vibe"
                  element={
                    <DeferredRoute>
                      <CustomVibePage />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/offers"
                  element={
                    <DeferredRoute>
                      <OffersPage />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/sale"
                  element={
                    <DeferredRoute>
                      <SalePage />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/shop"
                  element={
                    <DeferredRoute>
                      <ShopScreen />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/wishlist"
                  element={
                    <DeferredRoute>
                      <WishlistPage addToCart={addToCart} />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <DeferredRoute>
                      <AccountDashboard />
                    </DeferredRoute>
                  }
                />
                <Route
                  path="/account/vibe-check"
                  element={<div className="flex-1" />}
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </Suspense>
        </main>
      </React.Fragment>
    </WishlistProvider>
  )
}

export default App
